import React from 'react';
import { Container, Box, Typography, Card, CardContent, List, ListItem, ListItemIcon, ListItemText, Chip, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Helmet } from 'react-helmet';

export default function Guarantee() {
    return (
        <>
            <Helmet>
                <title>No Pass No Pay Guarantee - Louisiana Contractor's Licensing Service</title>
                <meta name="description" content="100% No Pass, No Pay Guarantee. We are committed to your success." />
            </Helmet>

            <Box
                sx={{
                    background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 50%, #ffa562 100%)',
                    color: 'white',
                    py: { xs: 6, md: 10 },
                    mb: 8,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
                    },
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <VerifiedIcon sx={{ fontSize: { xs: 60, md: 80 }, opacity: 0.9 }} />
                    </Box>
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 800,
                            textAlign: 'center',
                            mb: 2,
                            fontSize: { xs: '2.2rem', md: '3.5rem' },
                            textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                            letterSpacing: '-0.02em',
                        }}
                    >
                        No Pass, No Pay Guarantee
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            textAlign: 'center',
                            mb: 4,
                            opacity: 0.95,
                            fontWeight: 400,
                            fontSize: { xs: '1rem', md: '1.25rem' },
                        }}
                    >
                        Louisiana Contractor's Licensing Service
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Chip
                            label="100% Money Back Guarantee"
                            sx={{
                                background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                                color: '#1a1a1a',
                                fontSize: { xs: '1rem', md: '1.3rem' },
                                fontWeight: 800,
                                py: { xs: 2.5, md: 3.5 },
                                px: { xs: 3, md: 4 },
                                height: 'auto',
                                boxShadow: '0 8px 25px rgba(255, 215, 0, 0.5)',
                                border: '2px solid rgba(255, 255, 255, 0.3)',
                            }}
                        />
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ pb: 8 }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #fff5f0 0%, #ffffff 100%)',
                        borderRadius: 4,
                        p: { xs: 3, md: 5 },
                        mb: 6,
                        boxShadow: '0 4px 20px rgba(255, 94, 28, 0.08)',
                        border: '1px solid rgba(255, 94, 28, 0.1)',
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            textAlign: 'center',
                            mb: 2,
                            color: '#333',
                            lineHeight: 1.8,
                            fontWeight: 500,
                        }}
                    >
                        We are committed to your success. If you follow our program as designed and do not pass your official
                        licensing exam, you may qualify for a refund under the terms of our No Pass, No Pay Guarantee.
                    </Typography>
                </Box>

                <Typography
                    variant="h3"
                    sx={{
                        color: '#ff5e1c',
                        fontWeight: 700,
                        mb: 4,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        fontSize: { xs: '1.8rem', md: '2.5rem' },
                    }}
                >
                    <Box
                        sx={{
                            width: 8,
                            height: 45,
                            background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 100%)',
                            borderRadius: 2,
                        }}
                    />
                    Eligibility Requirements
                </Typography>

                <Typography variant="body1" sx={{ mb: 4, color: '#666', fontSize: '1.1rem' }}>
                    To request a refund under this guarantee, you must meet <strong>all</strong> of the following conditions:
                </Typography>

                <Box sx={{ mb: 5 }}>
                    <Card
                        sx={{
                            mb: 3,
                            background: 'linear-gradient(135deg, #ffffff 0%, #fff9f6 100%)',
                            borderLeft: '5px solid #ff5e1c',
                            borderRadius: 3,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 8px rgba(255, 94, 28, 0.08)',
                            '&:hover': {
                                transform: 'translateX(8px)',
                                boxShadow: '0 8px 30px rgba(255, 94, 28, 0.15)',
                                borderLeftWidth: '6px',
                            },
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#ff5e1c', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 100%)',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 36,
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '1rem'
                                }}>1</Box>
                                Official Exam Failure
                            </Typography>
                            <List>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Contact us within 5 days of failing your official exam"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333' }}
                                    />
                                </ListItem>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Provide a copy of the failure notice issued by the testing company"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333' }}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>

                    <Card
                        sx={{
                            mb: 3,
                            background: 'linear-gradient(135deg, #ffffff 0%, #fff9f6 100%)',
                            borderLeft: '5px solid #ff5e1c',
                            borderRadius: 3,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 8px rgba(255, 94, 28, 0.08)',
                            '&:hover': {
                                transform: 'translateX(8px)',
                                boxShadow: '0 8px 30px rgba(255, 94, 28, 0.15)',
                                borderLeftWidth: '6px',
                            },
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#ff5e1c', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 100%)',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 36,
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '1rem'
                                }}>2</Box>
                                Study Materials Requirement
                            </Typography>
                            <List>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="If books are required for your licensing exam, the book package must have been purchased (or rented) directly with your study program from Louisiana Contractor's Licensing Service"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333', lineHeight: 1.6 }}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>

                    <Card
                        sx={{
                            mb: 3,
                            background: 'linear-gradient(135deg, #ffffff 0%, #fff9f6 100%)',
                            borderLeft: '5px solid #ff5e1c',
                            borderRadius: 3,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 8px rgba(255, 94, 28, 0.08)',
                            '&:hover': {
                                transform: 'translateX(8px)',
                                boxShadow: '0 8px 30px rgba(255, 94, 28, 0.15)',
                                borderLeftWidth: '6px',
                            },
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#ff5e1c', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 100%)',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 36,
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '1rem'
                                }}>3</Box>
                                Practice Performance
                            </Typography>
                            <List>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Achieve at least 90% or higher on your practice exams"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333' }}
                                    />
                                </ListItem>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Complete at least one full-length quiz/practice test in the program"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333' }}
                                    />
                                </ListItem>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Take practice exams within 30 days prior to your official exam date"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333' }}
                                    />
                                </ListItem>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Spend at least 70% of the allowable exam time on practice tests (e.g., if the exam allows 100 minutes, spend at least 70 minutes on your attempt)"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333', lineHeight: 1.6 }}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>

                    <Card
                        sx={{
                            mb: 3,
                            background: 'linear-gradient(135deg, #ffffff 0%, #fff9f6 100%)',
                            borderLeft: '5px solid #ff5e1c',
                            borderRadius: 3,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 8px rgba(255, 94, 28, 0.08)',
                            '&:hover': {
                                transform: 'translateX(8px)',
                                boxShadow: '0 8px 30px rgba(255, 94, 28, 0.15)',
                                borderLeftWidth: '6px',
                            },
                        }}
                    >
                        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2.5, color: '#ff5e1c', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 100%)',
                                    color: 'white',
                                    borderRadius: '50%',
                                    width: 36,
                                    height: 36,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '1rem'
                                }}>4</Box>
                                Adequate Preparation
                            </Typography>
                            <List>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Cramming a few days before the exam is not considered adequate study time"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333' }}
                                    />
                                </ListItem>
                                <ListItem sx={{ py: 1 }}>
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <CheckCircleIcon sx={{ color: '#ff5e1c', fontSize: 28 }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="You must have reasonably followed the program as intended"
                                        primaryTypographyProps={{ fontSize: '1rem', color: '#333' }}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Box>

                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #fff9f6 0%, #ffe8dc 100%)',
                        border: '3px solid #ff5e1c',
                        borderRadius: 4,
                        mb: 5,
                        boxShadow: '0 8px 30px rgba(255, 94, 28, 0.15)',
                        overflow: 'hidden',
                        position: 'relative',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -50,
                            right: -50,
                            width: 200,
                            height: 200,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(255, 94, 28, 0.1) 0%, transparent 70%)',
                        }}
                    />
                    <CardContent sx={{ textAlign: 'center', py: { xs: 4, md: 6 }, px: { xs: 3, md: 5 }, position: 'relative' }}>
                        <Typography variant="h4" sx={{ color: '#ff5e1c', fontWeight: 800, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                            💰 Refund Structure
                        </Typography>
                        <Box
                            sx={{
                                background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 100%)',
                                borderRadius: '50%',
                                width: { xs: 140, md: 180 },
                                height: { xs: 140, md: 180 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 3,
                                boxShadow: '0 10px 40px rgba(255, 94, 28, 0.3)',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: '3.5rem', md: '4.5rem' },
                                    fontWeight: 900,
                                    color: 'white',
                                }}
                            >
                                100%
                            </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: '#333', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                            Full Refund of Your Prep Course Price
                        </Typography>
                        <Box
                            sx={{
                                background: 'rgba(255, 94, 28, 0.08)',
                                borderRadius: 3,
                                p: 2.5,
                                mt: 3,
                                border: '1px dashed #ff5e1c',
                            }}
                        >
                            <Typography variant="body1" sx={{ color: '#666', fontStyle: 'italic' }}>
                                <strong>Note:</strong> Refunds apply only to the prep course fee. The cost of books, tabs, or other materials is excluded.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Alert
                    severity="warning"
                    sx={{
                        mb: 5,
                        borderRadius: 3,
                        border: '2px solid #ff9800',
                        '& .MuiAlert-icon': {
                            fontSize: 28,
                        },
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        ⚠️ Important:
                    </Typography>
                    <Typography variant="body1">
                        Cramming a few days before the exam is not considered adequate study time. To qualify for this
                        guarantee, you must have reasonably followed the program as intended with sufficient preparation
                        time.
                    </Typography>
                </Alert>

                <Card
                    sx={{
                        background: 'linear-gradient(135deg, #ff5e1c 0%, #ff8c42 50%, #ffa562 100%)',
                        color: 'white',
                        borderRadius: 4,
                        boxShadow: '0 12px 40px rgba(255, 94, 28, 0.3)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'radial-gradient(circle at 70% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 50%)',
                        },
                    }}
                >
                    <CardContent sx={{ textAlign: 'center', py: { xs: 5, md: 6 }, px: { xs: 3, md: 5 }, position: 'relative', zIndex: 1 }}>
                        <EmailIcon sx={{ fontSize: { xs: 50, md: 70 }, mb: 2, opacity: 0.9 }} />
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                            How to Request a Refund
                        </Typography>
                        <Box sx={{ maxWidth: 600, margin: '0 auto', mb: 4 }}>
                            <Typography variant="h6" sx={{ mb: 2, opacity: 0.95, fontWeight: 400 }}>
                                Submit your refund request within 5 days of your official exam failure
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                Include your exam failure notice and confirmation of your qualifying practice test results
                            </Typography>
                        </Box>
                        <Box
                            component="a"
                            href="mailto:info@contractortestla.com"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1.5,
                                background: 'white',
                                color: '#ff5e1c',
                                textDecoration: 'none',
                                px: { xs: 3, md: 5 },
                                py: { xs: 2, md: 2.5 },
                                borderRadius: 50,
                                fontWeight: 700,
                                fontSize: { xs: '1rem', md: '1.2rem' },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                '&:hover': {
                                    transform: 'translateY(-3px)',
                                    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.25)',
                                    background: '#fff',
                                },
                            }}
                        >
                            <EmailIcon sx={{ fontSize: 28 }} />
                            info@contractortestla.com
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </>
    );
}
